import { useRef, useState, useEffect } from "react";
import { FaCamera, FaUser, FaBriefcase, FaUniversity, FaPhoneAlt } from "react-icons/fa";
import { useAuth } from "../context/authcontext";
import { GenderOptions } from "../../constants/constants";
import "./profile.css";

const formatDisplay = (val) => {
  if (val === null || val === undefined || val === "") return null;
  if (typeof val === "object") {
    return val.full_name || val.name || val.label || val.title || val.emp_id || null;
  }
  return String(val);
};

const formatDate = (val) => {
  const display = formatDisplay(val);
  if (!display) return null;
  const d = new Date(display);
  if (Number.isNaN(d.getTime())) return display;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
};

// Resolve the stored gender code/value against GenderOptions so we show
// "Male" instead of the raw "1", the same way the old Dropdown did.
const formatGender = (val) => {
  if (val === null || val === undefined || val === "") return null;
  const match = (GenderOptions || []).find(
    (opt) => String(opt.value) === String(val) || String(opt.label) === String(val)
  );
  return match ? match.label : String(val);
};

/* Reusable read-only detail cell */
function InfoItem({ label, value }) {
  return (
    <div className="info-items">
      <span className="info-items-label">{label}</span>
      <span className={`info-items-value ${!value ? "is-empty" : ""}`}>
        {value || "Not provided"}
      </span>
    </div>
  );
}

export default function PersonalInfo({ personal, onUploadPhoto, uploadingPhoto }) {
  const { hasPermission } = useAuth();
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  // Clear the pending selection once the upload finishes and the
  // server-confirmed photo comes back down through `personal.photoPreview`.
  useEffect(() => {
    if (!uploadingPhoto && pendingFile) {
      setPendingFile(null);
      if (pendingPreview) URL.revokeObjectURL(pendingPreview);
      setPendingPreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personal.photoPreview]);

  const getInitials = (firstName = "", lastName = "") =>
    `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();

  const applyFile = (file) => {
    if (!file?.type?.startsWith("image/")) return;
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(file);
    setPendingPreview(URL.createObjectURL(file));
  };

  const handlePhotoChange = (e) => {
    applyFile(e.target.files?.[0]);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    applyFile(e.dataTransfer.files?.[0]);
  };

  const handleUploadClick = () => {
    if (!pendingFile || uploadingPhoto) return;
    onUploadPhoto(pendingFile);
  };

  const cancelPending = () => {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(null);
    setPendingPreview(null);
  };

  const displayedPreview = pendingPreview || personal.photoPreview;

  const canViewPersonalDetails = hasPermission("employee.view_personal_details");
  const canViewBankDetails = hasPermission("employee.view_bank_details");

  const hasBankDetails =
    personal.bank_account_number || personal.ifsc_code || personal.bank_name;

  const hasEmergencyContact =
    personal.emergency_contact_name || personal.emergency_contact_number;

  return (
    <div className="account-card">
      <h2>Personal Information</h2>

      {/* BASIC INFORMATION — photo on the left, constant details on the right */}
      <div className="form-section-card">
        <div className="section-title-premium">
          <FaUser /> Basic Information
        </div>

        <div className="basic-info-layout">
          {/* Photo column */}
          <div className="avatar-basic-col">
            <div
              className={`avatar-frame ${dragging ? "dragging" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              title="Select a new photo"
            >
              <div className="avatar-ring">
                {displayedPreview ? (
                  <img src={displayedPreview} alt="profile" className="avatar-img" />
                ) : (
                  <div className="avatar-initials">
                    {getInitials(personal.first_name, personal.last_name) || "?"}
                  </div>
                )}
              </div>
              <span
                className="avatar-edit-badge"
                onClick={() => fileRef.current?.click()}
              >
                <FaCamera />
              </span>
            </div>

            {pendingFile ? (
              <div className="avatar-pending-actions">
                <button
                  type="button"
                  className="avatar-save-btn"
                  onClick={handleUploadClick}
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? (
                    <>
                      <span className="btn-spinner" /> Uploading…
                    </>
                  ) : (
                    "Upload"
                  )}
                </button>
                <button
                  type="button"
                  className="avatar-cancel-btn"
                  onClick={cancelPending}
                  disabled={uploadingPhoto}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <span className="avatar-hint">Click the camera icon to change photo</span>
            )}

            <input
              ref={fileRef}
              type="file"
              hidden
              accept="image/*"
              onChange={handlePhotoChange}
            />
          </div>

          {/* Details column */}
          <div className="basic-fields-col">
            <div className="info-grid">
              <InfoItem label="First Name" value={formatDisplay(personal.first_name)} />
              <InfoItem label="Last Name" value={formatDisplay(personal.last_name)} />
              <InfoItem label="Email" value={formatDisplay(personal.email)} />
              <InfoItem label="Phone Number" value={formatDisplay(personal.phone)} />
              {canViewPersonalDetails && (
                <InfoItem label="Gender" value={formatGender(personal.gender)} />
              )}
              {canViewPersonalDetails && (
                <InfoItem
                  label="Birthday"
                  value={formatDate(personal.date_of_birth || personal.birthday)}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* EMPLOYMENT DETAILS — read-only */}
      <div className="form-section-card">
        <div className="section-title-premium">
          <FaBriefcase /> Employment Details
        </div>

        <div className="info-grid">
          <InfoItem label="Employee ID" value={formatDisplay(personal.employee_id)} />
          <InfoItem label="Role" value={formatDisplay(personal.role)} />
          <InfoItem label="Designation" value={formatDisplay(personal.designation)} />
          <InfoItem label="Department" value={formatDisplay(personal.department)} />
          <InfoItem label="Joining Date" value={formatDate(personal.joining_date)} />
          <InfoItem
            label="Reporting Manager"
            value={formatDisplay(personal.reporting_manager)}
          />
        </div>
      </div>

      {/* BANK DETAILS — shown only if data exists */}
      {canViewBankDetails && hasBankDetails && (
        <div className="form-section-card">
          <div className="section-title-premium">
            <FaUniversity /> Bank Account Details
          </div>

          <div className="info-grid">
            <InfoItem
              label="Bank Account Number"
              value={formatDisplay(personal.bank_account_number)}
            />
            <InfoItem label="IFSC Code" value={formatDisplay(personal.ifsc_code)} />
            <InfoItem label="Bank Name" value={formatDisplay(personal.bank_name)} />
          </div>
        </div>
      )}

      {/* EMERGENCY CONTACT — shown only if data exists */}
      {canViewPersonalDetails && hasEmergencyContact && (
        <div className="form-section-card">
          <div className="section-title-premium">
            <FaPhoneAlt /> Emergency Contact
          </div>

          <div className="info-grid">
            <InfoItem
              label="Contact Name"
              value={formatDisplay(personal.emergency_contact_name)}
            />
            <InfoItem
              label="Contact Number"
              value={formatDisplay(personal.emergency_contact_number)}
            />
          </div>
        </div>
      )}
    </div>
  );
}